// THE contract between the shell and every src/features/<module>/index.tsx.
//
//   export default defineModule({
//     key: 'accounts', label: 'الحسابات', icon: Calculator,
//     flag: 'erp.accounts', minRole: 'staff', rail: 'accounts',
//     routes: [{ path: '/accounts/expenses', lazy: () => import('./screens/Expenses'), handle: gate({ minRole: 'manager' }) }],
//     menu:   [{ key: 'expenses', label: 'المصروفات', to: '/accounts/expenses', minRole: 'manager' }],
//   });
//
// Gating (rule 6 — a role or flag REMOVES the element, never locks it):
//   module.flag / module.minRole  → whole module absent from rail/menu; its
//                                   routes answer a deep link with <Forbidden/>.
//   MenuItem.flag/minRole/capability/modes → that menu row absent.
//   route.handle = gate({...})    → that route answers with <Forbidden/>.
//   anyOf (on a Gate or a module) → «role OR capability»: at least ONE of the
//                                   listed gates must pass, on top of the
//                                   AND of the other fields. E.g. inventory:
//                                   anyOf: [{ minRole: 'manager' }, { capability: 'inventory.write' }].
// Paths are ABSOLUTE (HANDOFF-erp-web §3 route map). The server enforces
// everything again — the client only hides.

import type { RouteObject } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import type { ErpMode } from '@/lib/env';
import type { Role } from '@/lib/me';
import type { TermKey } from '@/lib/terminology';

export type ModuleKey =
  | 'hotelRooms'
  | 'hotelReservations'
  | 'today'
  | 'calendar'
  | 'booking'
  | 'customers'
  | 'accounts'
  | 'inventory'
  | 'team'
  | 'reports'
  | 'tasks'
  | 'apps'
  | 'settings'
  | 'system';

/**
 * The nine rail entries (HANDOFF §0 rule 1) led by the two hotel entries
 * (HANDOFF §17: «الغرف» first; «الحجوزات» in the place of «المواعيد»), in
 * rail order. The hotel groups exist only while `erp.hotel` is on.
 */
export type RailGroup = 'rooms' | 'reservations' | 'appointments' | 'customers' | 'accounts' | 'inventory' | 'team' | 'reports' | 'tasks' | 'apps' | 'settings';

export const RAIL_ORDER: readonly RailGroup[] = ['rooms', 'reservations', 'appointments', 'customers', 'accounts', 'inventory', 'team', 'reports', 'tasks', 'apps', 'settings'];

/** A visibility rule. Every field is optional; all present fields must pass. */
export interface Gate {
  /** Feature flag that must be literally true in GET /v1/config (fail closed). */
  flag?: string | null;
  /** Lowest role allowed: staff < manager < owner. */
  minRole?: Role;
  /** Capability switch (owner always passes), e.g. 'accounts.manual_entry'. */
  capability?: string;
  /** Operating modes the element exists in (default: both). */
  modes?: ErpMode[];
  /**
   * Business sectors the element exists for (default: every sector), e.g. the
   * medical file only for clinic/dental. Refused like a switched-off feature.
   */
  sectors?: readonly string[];
  /**
   * OR-alternatives: at least one must pass (every other field still must).
   * Empty/absent = no extra condition. When none passes the denial is the
   * FIRST alternative's (so list the role alternative first: its
   * «للمدير فما فوق» sentence is the one a refused deep link shows).
   */
  anyOf?: Gate[];
}

export interface MenuItem extends Gate {
  key: string;
  /** Arabic label exactly as HANDOFF §3. */
  label: string;
  /** Use a terminology token instead of `label` (e.g. 'customers' → المراجعون/الزبائن). */
  termLabel?: TermKey;
  /**
   * Absolute path. A query names a view («/hotel/reservations?view=arrivals»):
   * the row is current only when the location carries those parameters.
   */
  to: string;
  /**
   * Extra paths this row is the current page for, beyond `to` and its
   * sub-paths (':param' = one segment, a trailing '*' = the rest), e.g.
   * «الملف الطبي» on '/customers/:customerId/medical/*'.
   */
  match?: string[];
  /**
   * An eyebrow drawn above this row when it opens a new section of the
   * column (board 27: «الإعداد» before the hotel's setup rows, «الشركات»
   * before «الوفود والشركات»). The rows of one section carry the same text;
   * the eyebrow renders before the first VISIBLE row of the section, so a
   * section whose rows are all gated away leaves no eyebrow (rule 6).
   */
  section?: string;
}

/** What a route may carry in `handle` (use the gate() helper). */
export interface ErpRouteHandle {
  gate?: Gate;
  /** Set by the shell on each module's parent route. */
  moduleKey?: ModuleKey;
}

export interface ErpModule {
  key: ModuleKey;
  /** Rail label (HANDOFF §0 rule 1); modules sharing a rail group share the first one's label. */
  label: string;
  termLabel?: TermKey;
  icon: LucideIcon;
  /** Module flag (null = always on, e.g. system). */
  flag: string | null;
  minRole: Role;
  /** OR-alternatives on the whole module (see Gate.anyOf), e.g. manager+ OR a capability switch. */
  anyOf?: Gate[];
  /** Which rail entry this module lives under (null = no rail entry, e.g. system). */
  rail: RailGroup | null;
  /**
   * A rail group this module takes the place of while it is visible (HANDOFF
   * §17: «الحجوزات» replaces «المواعيد» — today · calendar · booking leave the
   * rail, their routes stay). Off (flag or role), the group is back.
   */
  replacesRail?: RailGroup;
  /** Absolute-path routes rendered inside the shell's content area. */
  routes: RouteObject[];
  /** Module-menu rows (the 200 px column / rail flyout / phone sheet). */
  menu: MenuItem[];
}

/** Identity helper that type-checks a module definition. */
export function defineModule(m: ErpModule): ErpModule {
  return m;
}

/** Route handle helper: `handle: gate({ minRole: 'owner' })`. */
export function gate(g: Gate): ErpRouteHandle {
  return { gate: g };
}
